import React from 'react';
import Section from './Section';
import ValidationStep from '../wizard/validation/ValidationStep';
import { ComponentAlternative, ValidationResult } from '../wizard/validation/types';

/**
 * /design — Wizard Step 3b validation states. Compare with
 * `design/RFQ Wizard v2 Step 3b.dc.html`. Static sample data shaped
 * exactly like today's validate-and-recommend output; note the mock's
 * per-swap confidence chips are absent on purpose (the engine only scores
 * the overall recommendation).
 */

const RULE_RESULTS: ValidationResult[] = [
  {
    ruleName: 'voltage_mismatch_risk',
    ruleType: 'soft',
    status: 'warn',
    message: 'Motor driver is rated 100V max, but the BMS package allows regen spikes up to 110V under full-load braking.',
    affectedComponents: ['DRV-8353-EVM'],
    recommendation: 'Add transient voltage protection between the BMS and the motor driver.',
    suggestedComponentsToAdd: [
      { name: 'TVS clamp diode', specifications: 'Standoff 100V, peak pulse 110V+', quantity: 1, manufacturer: 'Generic / Standard' },
    ],
  },
  {
    ruleName: 'missing_connector_spec',
    ruleType: 'hard',
    status: 'fail',
    message: 'Power cable run has no mating connector listed — suppliers will quote incomplete assemblies.',
    affectedComponents: ['CBL-PWR-AWG12'],
    recommendation: 'List the mating connector pair so quotes cover the full assembly.',
    suggestedComponentsToAdd: [
      { name: 'XT90 connector pair', specifications: 'Rated 90A continuous, 12AWG compatible', quantity: 2, manufacturer: 'Generic / Standard' },
    ],
  },
  {
    ruleName: 'duty_cycle_underspecified',
    ruleType: 'soft',
    status: 'warn',
    message: 'Stated duty cycle (8 h/day) is below what the hub-motor thermal rating supports for this payload.',
    affectedComponents: ['Hub Motor 48V'],
    recommendation: 'A 16 h/day duty cycle matches the drive-train rating and the AMR use case.',
    suggestedValue: '16',
    affectedField: 'dutyCycle',
  },
  {
    ruleName: 'no_machine_fix_example',
    ruleType: 'info',
    status: 'info',
    message: 'Two encoders share one part number but list different PPR values in their specifications.',
    affectedComponents: ['ENC-2048-A'],
    recommendation: 'Confirm which PPR value is intended before outreach — I cannot pick for you.',
  },
  ...['quantities_consistent', 'units_recognized', 'no_duplicate_parts', 'lead_time_feasible', 'compliance_coverage', 'power_budget_balanced'].map(
    (ruleName): ValidationResult => ({
      ruleName,
      ruleType: 'soft',
      status: 'pass',
      message: 'Check passed.',
      affectedComponents: [],
    }),
  ),
];

const ALTERNATIVES: ComponentAlternative[] = [
  {
    originalComponentName: 'Optical encoder, 2048 PPR',
    issue: 'Optical encoders fail early in dusty warehouse air.',
    improvementReason: 'A sealed magnetic encoder doubles resolution and removes the dust-ingress failure mode.',
    alternatives: [
      {
        name: 'Magnetic encoder, 4096 PPR',
        reason: 'Sealed against dust, double the resolution, and widely stocked.',
        specifications: '4096 PPR, IP67, SSI/ABZ output',
        manufacturer: 'Generic / Standard',
        targetPrice: 4200,
      },
    ],
  },
  {
    originalComponentName: 'IMU-BNO085',
    issue: 'The BNO085 is entering end-of-life; long lead times are already common.',
    improvementReason: 'The BNO086 is a drop-in replacement with better availability.',
    alternatives: [
      {
        name: 'Pin-compatible BNO086',
        reason: 'Same footprint and protocol, actively produced.',
        specifications: '9-DOF IMU, I2C/SPI/UART',
        manufacturer: 'CEVA',
        targetPrice: 1650,
      },
    ],
  },
];

const COMPONENTS = [
  { name: 'DRV-8353-EVM' },
  { name: 'CBL-PWR-AWG12' },
  { name: 'Hub Motor 48V' },
  { name: 'ENC-2048-A' },
  { name: 'Optical encoder, 2048 PPR' },
  {
    name: 'IMU-BNO085',
    replacementHistory: [
      { name: 'IMU-BNO085', reason: 'Original BOM entry', changedAt: '', version: 1 },
      { name: 'Pin-compatible BNO086', reason: 'Swapped for availability', changedAt: '2026-07-20T10:00:00Z', version: 2 },
    ],
    currentVersionIndex: 0,
  },
];

const AI_SUGGESTION = {
  title: 'Solid drive-train BOM with two sourcing risks',
  summary: 'The BOM is complete enough to source once the regen-voltage clamp and the missing connector are addressed. The encoder and IMU swaps are optional but cut lead-time risk.',
  reasoning: 'Both warnings block clean quotes rather than function: suppliers either exclude protection parts or quote incomplete cable assemblies. The improvement swaps target the two components with the worst availability signals.',
  tradeoffs: ['The magnetic encoder changes the mounting interface', 'BNO086 firmware needs a version-string check'],
  confidence: 0.85,
};

const resolve = () => Promise.resolve();
const never = () => new Promise<void>(() => undefined);
const noop = () => undefined;

const ValidationStepGallery: React.FC = () => (
  <>
    <Section title="Wizard step 3b — fixes, improvements, Robbie's read">
      <div className="mx-auto max-w-content">
        <ValidationStep
          ruleResults={RULE_RESULTS}
          componentAlternatives={ALTERNATIVES}
          aiSuggestion={AI_SUGGESTION}
          components={COMPONENTS}
          onRunValidation={resolve}
          onReplaceComponent={resolve}
          onRevertComponent={resolve}
          onAddComponent={resolve}
          onApplySuggestion={resolve}
          onNext={noop}
          onPrevious={noop}
        />
      </div>
    </Section>
    <Section title="Wizard step 3b — all checks pass · checks running">
      <div className="mx-auto flex max-w-content flex-col gap-s7">
        <ValidationStep
          ruleResults={RULE_RESULTS.filter((r) => r.status === 'pass')}
          componentAlternatives={[]}
          aiSuggestion={null}
          components={COMPONENTS}
          onRunValidation={resolve}
          onReplaceComponent={resolve}
          onRevertComponent={resolve}
          onAddComponent={resolve}
          onApplySuggestion={resolve}
          onNext={noop}
          onPrevious={noop}
        />
        <ValidationStep
          ruleResults={[]}
          componentAlternatives={[]}
          aiSuggestion={null}
          components={[]}
          onRunValidation={never}
          onReplaceComponent={resolve}
          onRevertComponent={resolve}
          onAddComponent={resolve}
          onApplySuggestion={resolve}
          onNext={noop}
          onPrevious={noop}
        />
      </div>
    </Section>
  </>
);

export default ValidationStepGallery;
